import SocketService from "@/services/Socket"
import ErrorHandlerService from "@/services/ErrorHandler"

import useUnmount from "@/hooks/useUnmount"
import useConstantValue from "@/hooks/useConstantValue"

import {
	ExtraSocketClientEvents,
	ExtraSocketServerEvents,
	OnClearWhatsappSessionEventResponse,
	OnQRCodeGeneratedEventResponse,
	OnWhatsappHistorySyncProgressEventResponse,
	OnStartWhatsappSessionEventResponse
} from "@/@integrations/Whatsapp/protocols/channel"
import { ErrorType } from "@/hooks/useValidation"

const useWhatsappConnection = () => {
	const socket = useConstantValue(new SocketService())

	const startSession = async () => {
		try {
			const { result } = await socket.emit<OnStartWhatsappSessionEventResponse, ExtraSocketServerEvents>(
				"StartWhatsappSession",
				"whatsapp",
				{}
			)

			if (result === "SessionCreated") {
				return true
			} else {
				return false
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const clearSession = async (integrationId: string) => {
		try {
			await socket.emit<OnClearWhatsappSessionEventResponse, ExtraSocketServerEvents>(
				"ClearWhatsappSession",
				"whatsapp",
				{
					inboxChannel: {
						integrationId
					}
				}
			)

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const forceReconnectSession = async (integrationId: string) => {
		try {
			await socket.emit<OnClearWhatsappSessionEventResponse, ExtraSocketServerEvents>(
				"ForceReconnectWhatsappSession",
				"whatsapp",
				{
					inboxChannel: {
						integrationId
					}
				}
			)

			return true
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return false
		}
	}

	const onQRCodeGenerated = (callback: (src: string) => void) => {
		socket.on<OnQRCodeGeneratedEventResponse, ExtraSocketClientEvents>("QRCodeGenerated", (data) => {
			const formattedBase64Image = `data:image/png;base64, ${data.QRCodeBase64Image}`

			callback(formattedBase64Image)
		})
	}

	const onWhatsappAuthenticated = (callback: () => void) => {
		socket.on<null, ExtraSocketClientEvents>("WhatsappAuthenticated", callback)
	}

	const onQRCodeScanned = (callback: () => void) => {
		socket.on<null, ExtraSocketClientEvents>("QRCodeScanned", callback)
	}

	const onWhatsappDisconnected = (callback: () => void) => {
		socket.on<null, ExtraSocketClientEvents>("WhatsappDisconnected", callback)
	}

	const onWhatsappLoggedOut = (callback: () => void) => {
		socket.on<null, ExtraSocketClientEvents>("WhatsappLoggedOut", callback)
	}

	const onQRCodeFailed = (callback: () => void) => {
		socket.on<null, ExtraSocketClientEvents>("QRCodeFailed", callback)
	}

	const onQRCodeTimedOut = (callback: () => void) => {
		socket.on<null, ExtraSocketClientEvents>("QRCodeTimedOut", callback)
	}

	const onWhatsappHistorySyncProgress = (callback: (progressInPercentage: number) => void) => {
		socket.on<OnWhatsappHistorySyncProgressEventResponse, ExtraSocketClientEvents>("WhatsappHistorySyncProgress", (data) => {
			callback(data.historySyncProgressInPercentage)
		})
	}

	useUnmount(() => {
		socket.dispose()
	})

	return {
		onQRCodeScanned,
		onWhatsappAuthenticated,
		onQRCodeGenerated,
		onWhatsappDisconnected,
		onWhatsappLoggedOut,
		onQRCodeFailed,
		onQRCodeTimedOut,
		onWhatsappHistorySyncProgress,
		startSession,
		clearSession,
		forceReconnectSession
	}
}

export default useWhatsappConnection
